import isCdsFrequentPassengerOptInEligible from 'utils/isCdsFrequentPassengerOptInEligible';

/**
 * Derive Quick Purchase agreement visibility and canonical writer applicability.
 * Location only governs rendering and the local legal requirement.
 * @param {object} options - Tenant flags, brand, and current plain CDS session.
 * @param {object} options.features - Tenant agreement flags.
 * @param {string} options.brand - Tenant slug.
 * @param {object} options.cdsSession - Current plain CDS bootstrap/authentication state.
 * @returns {object} Agreement policy for fields and payment synchronization.
 */
export function getQuickCheckoutAgreementPolicy({
  features,
  brand,
  cdsSession,
  usesReserhubAccount,
  ...account
}) {
  const atCheckout = features.POLICIES_DISCLAIMER_LOCATION === 'CHECKOUT';
  const promotionalTermsApplicable = Boolean(features.SHOW_PROMOTIONAL_TERMS_CHECKBOX);
  const cdsFrequentPassengerOptInApplicable = isCdsFrequentPassengerOptInEligible({
    features,
    brand,
    cdsSession: {
      ...cdsSession,
      usesReserhubAccount: usesReserhubAccount ?? cdsSession?.usesReserhubAccount,
    },
    ...account,
  });
  const legalRequired = atCheckout && Boolean(features.SHOW_LEGAL_TERMS_CHECKBOX);
  const showPromotionalTerms = atCheckout && promotionalTermsApplicable;
  const showCdsFrequentPassengerOptIn = atCheckout && cdsFrequentPassengerOptInApplicable;

  return {
    legalRequired,
    showPromotionalTerms,
    showCdsFrequentPassengerOptIn,
    hasAgreements: legalRequired || showPromotionalTerms || showCdsFrequentPassengerOptIn,
    promotionalTermsApplicable,
    cdsFrequentPassengerOptInApplicable,
  };
}

/**
 * Compare explicit optional choices against the meta updated by updatePurchaseFields.
 * Missing fields do not revoke stored choices; legal is never a writable agreement.
 * @param {object} options - Policy, writable form choices, and purchase meta.
 * @param {object} options.policy - Current agreement applicability.
 * @param {object} options.purchaserFormValues - Current writable form values.
 * @param {object} options.meta - Stored purchase agreement meta.
 * @returns {boolean} Whether an applicable current choice still needs saving.
 */
export function areQuickCheckoutAgreementsDirty({ policy, purchaserFormValues = {}, meta = {} }) {
  return (
    (policy.promotionalTermsApplicable &&
      purchaserFormValues.promotionalTerms !== undefined &&
      Boolean(purchaserFormValues.promotionalTerms) !==
        (meta.accepted_promotional_terms === 'true')) ||
    (policy.cdsFrequentPassengerOptInApplicable &&
      purchaserFormValues.cdsFrequentPassengerOptIn !== undefined &&
      Boolean(purchaserFormValues.cdsFrequentPassengerOptIn) !==
        (meta.frequent_passenger_enrollment === 'true'))
  );
}

/**
 * Select the account inputs shared by rendered and post-await agreement eligibility.
 * @param {object} state - Initialized Redux state.
 * @returns {object} Canonical account eligibility inputs.
 */
export const selectQuickCheckoutAgreementAccount = (state) => ({
  usesReserhubAccount: state.cdsFrequentPassenger.get('usesReserhubAccount'),
  reserhubUser: state.reserhubAccount?.get('user'),
  isReserhubUserLoading: state.reserhubAccount?.get('isUserLoading'),
  isReserhubUserFetched: state.reserhubAccount?.get('isUserFetched'),
  isReserhubUserAnonymous: state.reserhubAccount?.get('isUserAnonymous'),
});
